import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client.ts'
import type { Family, FamilyMember } from '../api/types.ts'
import { lastSeenText } from '../lib/format.ts'
import { useAuth } from '../store/auth.ts'
import { useChats } from '../store/chats.ts'
import { onServerEvent } from '../realtime/socket.ts'
import { Avatar } from './Avatar.tsx'

/**
 * КОНТАКТЫ
 *
 * Все люди семьи — и с каждым можно поговорить один на один. Раньше
 * до личного разговора добирались через «Новая переписка»: открыть,
 * найти человека, отметить, нажать «создать». Выглядело это как
 * заведение чего-то нового, хотя человек просто хотел написать маме.
 *
 * Здесь нажатие на человека сразу открывает разговор с ним. Если
 * такого разговора ещё не было — он заводится молча, в тот же миг.
 * Второго не появится: сервер находит уже существующий.
 *
 * Порядок — кто в сети наверху, дальше по давности: так видно, кому
 * написать прямо сейчас, а кто прочтёт потом.
 */

const ROLE_NAMES: Record<FamilyMember['role'], string> = {
  OWNER: 'владелец',
  ADULT: 'взрослый',
  TEEN: 'подросток',
  CHILD: 'ребёнок',
}

export function ContactList({ query }: { query: string }) {
  const me = useAuth((s) => s.user)
  const createGroup = useChats((s) => s.createGroup)
  const navigate = useNavigate()

  const [members, setMembers] = useState<FamilyMember[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)

  useEffect(() => {
    let alive = true

    const load = () =>
      api<{ family: Family }>('/api/family')
        .then((data) => alive && setMembers(data.family.members))
        .catch((e) => alive && setError(e instanceof Error ? e.message : 'Не удалось загрузить'))

    void load()

    // Кто пришёл и ушёл — сервер сообщает сразу
    const off = onServerEvent((event) => {
      if (event.type !== 'user:presence') return
      setMembers((list) =>
        list?.map((m) =>
          m.id === event.userId ? { ...m, online: event.online, lastSeenAt: event.lastSeenAt } : m,
        ) ?? list,
      )
    })

    // И на всякий случай раз в минуту перечитываем целиком
    const timer = window.setInterval(() => void load(), 60_000)
    return () => {
      alive = false
      off()
      window.clearInterval(timer)
    }
  }, [])

  const people = useMemo(() => {
    const text = query.trim().toLowerCase()

    return (members ?? [])
      .filter((m) => m.id !== me?.id)
      .filter((m) => !text || m.displayName.toLowerCase().includes(text))
      .sort((a, b) => {
        if (a.online !== b.online) return a.online ? -1 : 1
        const at = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
        const bt = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
        if (at !== bt) return bt - at
        return a.displayName.localeCompare(b.displayName, 'ru')
      })
  }, [members, me?.id, query])

  /**
   * Открыть личный разговор.
   *
   * Спрашиваем сервер, а не ищем у себя по имени: двое в семье вполне
   * могут зваться одинаково, и тогда открылся бы разговор не с тем.
   * Сервер находит существующий разговор по людям, а не по названию,
   * и заводит новый, только если его действительно не было.
   */
  const open = async (person: FamilyMember) => {
    setOpening(person.id)
    setError(null)
    try {
      const chatId = await createGroup('', [person.id])
      navigate(`/chat/${chatId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось открыть разговор')
    } finally {
      setOpening(null)
    }
  }

  if (error && !members) {
    return <p className="empty-hint">{error}</p>
  }

  if (!members) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    )
  }

  if (people.length === 0) {
    return (
      <p className="empty-hint">
        {query.trim()
          ? 'Никого не нашлось'
          : 'В семье пока только ты. Позови своих: меню → «Семья и приглашения»'}
      </p>
    )
  }

  return (
    <>
      {error && <div className="sheet-error contact-error">{error}</div>}

      {people.map((person) => (
        <button
          key={person.id}
          className="chat-row contact-row"
          onClick={() => void open(person)}
          disabled={opening !== null}
        >
          <Avatar
            id={person.id}
            name={person.displayName}
            url={person.avatarUrl}
            online={person.online}
          />
          <div className="chat-row-body">
            <div className="chat-row-top">
              <span className="chat-row-title">{person.displayName}</span>
              <span className="contact-role">{ROLE_NAMES[person.role]}</span>
            </div>
            <div className="chat-row-bottom">
              <span className={'chat-row-preview' + (person.online ? ' is-online' : '')}>
                {opening === person.id
                  ? 'открываю разговор…'
                  : lastSeenText(person.lastSeenAt, person.online)}
              </span>
            </div>
          </div>
        </button>
      ))}
    </>
  )
}
